import { Trash } from '@phosphor-icons/react';
import translations from '../../translations';
import type { IPRangeAPI, SecuritySettingsAPI } from '../../types/Tenant';
import Table, { type TableColumnList } from '../generic/Table';
import TextEditor from './TextEditor';
import type { UpdateTenant } from './TenantPage';

type IPRange = keyof {
    [Key in keyof SecuritySettingsAPI as Key extends `authorized${infer _}`
        ? Key
        : never]: SecuritySettingsAPI[Key];
};

type Restriction = keyof {
    [Key in keyof SecuritySettingsAPI as Key extends `is${infer _}RestrictedByIPRange`
        ? Key
        : never]: SecuritySettingsAPI[Key];
};

type SecurityIPSettingProps = {
    ipRange: IPRange;
    isActive: boolean;
    settingPropertyName: Restriction;
    rangeEntries: IPRangeAPI[];
    labelText: string;
    updateTenant: UpdateTenant;
};

const SecurityIPSetting = ({
    ipRange,
    isActive,
    settingPropertyName,
    rangeEntries,
    labelText,
    updateTenant,
}: SecurityIPSettingProps) => {
    const addIPRange = (entry: IPRangeAPI) => {
        const updatedRange = [...rangeEntries, entry];

        updateTenant({
            securitySettings: {
                [ipRange]: updatedRange,
            },
        });
    };

    const updateIPRange = (index: number, entry: IPRangeAPI) => {
        let updatedRange = rangeEntries;
        if (rangeEntries[index]) {
            updatedRange[index] = entry;
        } else {
            updatedRange = [...rangeEntries, entry];
        }

        updateTenant({
            securitySettings: {
                [ipRange]: updatedRange,
            },
        });
    };

    const removeIPRange = (index: number) => {
        const updatedRange = rangeEntries.filter((_, entryIndex) => entryIndex !== index);

        updateTenant({
            securitySettings: {
                [ipRange]: updatedRange,
            },
        });
    };

    const columns: TableColumnList<IPRangeAPI> = [
        {
            renderHeader: () => (
                <span id="ip-range-entry-name">{translations.COMMON_TABLE_name}</span>
            ),
            renderCell: ({ item: rangeEntry, rowIndex }) => (
                <TextEditor
                    labelledBy="ip-range-entry-name"
                    text={rangeEntry.developerName}
                    autofocus
                    onChange={({ target: { value } }) => {
                        updateIPRange(rowIndex, {
                            ...rangeEntries[rowIndex],
                            developerName: value,
                        });
                    }}
                />
            ),
        },
        {
            renderHeader: () => (
                <span id="ip-range-entry-summary">{translations.COMMON_TABLE_summary}</span>
            ),
            renderCell: ({ item: rangeEntry, rowIndex }) => (
                <TextEditor
                    labelledBy="ip-range-entry-summary"
                    text={rangeEntry.developerSummary}
                    onChange={({ target: { value } }) => {
                        updateIPRange(rowIndex, {
                            ...rangeEntries[rowIndex],
                            developerSummary: value,
                        });
                    }}
                />
            ),
        },
        {
            renderHeader: () => (
                <span id="ip-range-entry-start-address">
                    {translations.COMMON_TABLE_start_ip_address}
                </span>
            ),
            renderCell: ({ item: rangeEntry, rowIndex }) => (
                <TextEditor
                    labelledBy="ip-range-entry-start-address"
                    text={rangeEntry.startIPAddress}
                    onChange={({ target: { value } }) => {
                        updateIPRange(rowIndex, {
                            ...rangeEntries[rowIndex],
                            startIPAddress: value,
                        });
                    }}
                />
            ),
        },
        {
            renderHeader: () => (
                <span id="ip-range-entry-end-address">
                    {translations.COMMON_TABLE_end_ip_address}
                </span>
            ),
            renderCell: ({ item: rangeEntry, rowIndex }) => (
                <TextEditor
                    labelledBy="ip-range-entry-end-address"
                    text={rangeEntry.endIPAddress}
                    onChange={({ target: { value } }) => {
                        updateIPRange(rowIndex, {
                            ...rangeEntries[rowIndex],
                            endIPAddress: value,
                        });
                    }}
                />
            ),
        },
        {
            renderHeader: () => translations.COMMON_TABLE_actions,
            renderCell: ({ rowIndex }) => (
                <div className="action-btn-wrapper">
                    <button
                        className="table-icon table-icon-delete"
                        onClick={() => {
                            removeIPRange(rowIndex);
                        }}
                        type="button"
                    >
                        <Trash />
                    </button>
                </div>
            ),
            size: '5rem',
        },
    ];

    return (
        <>
            <div className="checkbox">
                <label>
                    <input
                        className="ip-setting-enable"
                        type="checkbox"
                        checked={isActive}
                        onChange={({ target: { checked } }) => {
                            updateTenant({
                                securitySettings: {
                                    [settingPropertyName]: checked,
                                },
                            });
                        }}
                    />
                    {labelText}
                </label>
            </div>
            <div className="security-ip-range" hidden={!isActive} data-testid="security-ip-range">
                <Table columns={columns} items={rangeEntries} />
                <button
                    className="link-emulate add-range"
                    onClick={() => {
                        addIPRange({
                            developerName: '',
                            developerSummary: '',
                            startIPAddress: '',
                            endIPAddress: '',
                        });
                    }}
                    type="button"
                >
                    <span className="glyphicon glyphicon-plus-sign" />
                    {translations.TENANT_security_add_ip_range_button_label}
                </button>
            </div>
        </>
    );
};

export default SecurityIPSetting;
